import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "../../lib/firebase";

export interface ContactSaveEvent {
  id: string;
  vcardId: string;
  vcardCategory: string;
  savedAt: Date;
  userAgent?: string;
  referrer?: string;
}

export interface ContactAnalytics {
  totalSaves: number;
  recentSaves: ContactSaveEvent[];
  loading: boolean;
  error: string | null;
}

interface RecordContactSaveResult {
  success: boolean;
  status?: string;
  message?: string;
}

const rawEnv = import.meta.env as unknown as Record<string, unknown>;

const getEnvString = (key: string): string => {
  const value = rawEnv[key];
  return typeof value === "string" ? value.trim() : "";
};

const resolveContactSaveEndpoint = (): string => {
  const explicitEndpoint = getEnvString("VITE_CONTACT_SAVE_API_URL");
  if (explicitEndpoint) {
    return explicitEndpoint;
  }

  const projectId = getEnvString("VITE_FIREBASE_PROJECT_ID");
  if (projectId) {
    return `https://us-central1-${projectId}.cloudfunctions.net/recordContactSave`;
  }

  return "/api/record-contact-save";
};

const CONTACT_SAVE_API_URL = resolveContactSaveEndpoint();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const toDateOrNull = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (isRecord(value) && "toDate" in value) {
    const maybeToDate = value.toDate;
    if (typeof maybeToDate === "function") {
      try {
        const parsed: unknown = maybeToDate.call(value);
        if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      } catch {
        return null;
      }
    }
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const parseContactSaveEvent = (
  doc: QueryDocumentSnapshot<DocumentData>,
): ContactSaveEvent | null => {
  const data = doc.data();

  if (!data || typeof data !== "object") {
    return null;
  }

  const savedAt = toDateOrNull(data.savedAt);
  if (!savedAt) {
    return null;
  }

  return {
    id: doc.id,
    vcardId: (data.vcardId as string) || "",
    vcardCategory: (data.vcardCategory as string) || "",
    savedAt,
    userAgent: (data.userAgent as string | undefined) || undefined,
    referrer: (data.referrer as string | undefined) || undefined,
  };
};

export const fetchUserContactAnalytics = async (
  userId: string,
): Promise<{ totalSaves: number; recentSaves: ContactSaveEvent[] }> => {
  if (!userId) {
    return {
      totalSaves: 0,
      recentSaves: [],
    };
  }

  try {
    // Query contact save events for this user
    const contactSavesQuery = query(
      collection(db, "users", userId, "contactSaves"),
      orderBy("savedAt", "desc"),
      limit(100), // Get last 100 saves
    );

    const querySnapshot = await getDocs(contactSavesQuery);

    const saveEvents: ContactSaveEvent[] = [];
    querySnapshot.forEach((doc) => {
      const parsed = parseContactSaveEvent(doc);
      if (parsed) {
        saveEvents.push(parsed);
      }
    });

    return {
      totalSaves: saveEvents.length,
      recentSaves: saveEvents,
    };
  } catch (error) {
    console.error("Error fetching contact analytics:", error);
    return {
      totalSaves: 0,
      recentSaves: [],
    };
  }
};

export const recordContactSave = async (
  vcardId: string,
  vcardCategory: string,
  userId: string,
): Promise<void> => {
  if (!vcardId || !userId) {
    throw new Error("vcardId and userId are required");
  }

  try {
    const response = await fetch(CONTACT_SAVE_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        vcardId,
        vcardCategory,
        userId,
      }),
    });

    let result: RecordContactSaveResult | null = null;
    try {
      result = (await response.json()) as RecordContactSaveResult;
    } catch {
      result = null;
    }

    if (!response.ok || result?.success === false) {
      throw new Error(
        result?.message || `Contact save failed with status ${response.status}`,
      );
    }
  } catch (error) {
    console.error("Error recording contact save:", error);
    throw error;
  }
};
